import { userAssetsStore } from "@/stores/userAssets";
import { GenerateBizType } from "@/types/creator";
import { isAuthUser, isVip } from "@/utils/status";
import { useMemo } from "react";
import {
  GeneratePriceProps,
  generateTaskCost,
  progressPriceGenerator,
} from "../utils/generateCost";

export function useGenerateCost({
  generateCount,
  size,
  calcPriceProps,
  useCoin = "total",
  cost,
}: {
  generateCount?: number;
  size?: { w: number; h: number };
  calcPriceProps?: Omit<GeneratePriceProps, "size">;
  useCoin?: "total" | "payed";
  cost?: number;
}) {
  const { baseSize, basePrice = 0, unitPrice = 2, fixPrice } = calcPriceProps ?? {};
  const { heartCoinInfo } = userAssetsStore;

  const _cost = useMemo(
    () =>
      cost ??
      generateTaskCost({
        unitPrice: progressPriceGenerator({
          size: size ?? { w: 0, h: 0 },
          unitPrice,
          baseSize,
          basePrice,
          fixPrice,
        }),
        count: generateCount,
      }),
    [cost, size, generateCount],
  );

  const affordable = useMemo(() => {
    if (!isAuthUser()) {
      return true;
    }
    if (useCoin === "payed") {
      return _cost <= heartCoinInfo[useCoin];
    }
    return isVip() || _cost <= heartCoinInfo[useCoin ?? "total"];
  }, [heartCoinInfo, cost]);

  return {
    cost: _cost,
    affordable,
  };
}

const NoliboxAIMetaTypeDefaultPriceConfig = {
  basePrice: 0,
  unitPrice: 2,
};

export const GeneratePriceRules: Record<
  GenerateBizType | ("Matt" | "Monkey" | "Wenxin"),
  Omit<GeneratePriceProps, "size">
> = {
  [GenerateBizType.Presets]: {
    basePrice: 1,
    unitPrice: 2,
  },
  [GenerateBizType.Txt2Img]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.Img2Img]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.Variation]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.SuperRsolution]: {
    fixPrice: 2,
  },
  [GenerateBizType.Replace]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.Erase]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.Landscape]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.NegativePrompt]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.Outpainting]: NoliboxAIMetaTypeDefaultPriceConfig,
  [GenerateBizType.OutpaintingPipline]: NoliboxAIMetaTypeDefaultPriceConfig,
  Matt: { fixPrice: 2 },
  Monkey: {
    basePrice: 0,
    unitPrice: 3,
  },
  Wenxin: {
    fixPrice: 10,
  },
};
